import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Spin, Tooltip } from 'antd';
import styled from 'styled-components';

import { PATHS } from 'router/config';
import { PopupNames } from 'core';
import { ZoomStatus } from 'types';
import {
  useLessonRecording,
  useZoomMutations,
  useConditionalPolling,
  usePopupStateOperations,
} from 'core/hooks';
import { formatWarsaw } from 'core/utils/zoomTime';
import {
  PipelineStepper,
  AiSummaryViewer,
  ZoomStatusBadge,
  ProcessingLogTimeline,
} from 'molecules';
import { Colors } from 'services/ColorService';
import { getFont } from 'services/FontService';

const POLL_INTERVAL = 10_000;
const REFETCH_DELAY = 3_000;

export const LessonCard = memo(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const {
    lesson,
    recording,
    loading,
    refetch,
    startPolling,
    stopPolling,
  } = useLessonRecording(id);
  const mutations = useZoomMutations();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const status = (recording?.status as unknown) as ZoomStatus | undefined;

  const isPollable = useMemo(
    () =>
      status === ZoomStatus.DOWNLOADING ||
      status === ZoomStatus.UPLOADED ||
      status === ZoomStatus.AI_PROCESSING ||
      status === ZoomStatus.FAILED,
    [status],
  );

  useConditionalPolling({
    startPolling,
    stopPolling,
    pollInterval: POLL_INTERVAL,
    predicate: isPollable,
  });

  const refetchSoon = useCallback(() => {
    window.setTimeout(refetch, REFETCH_DELAY);
  }, [refetch]);

  const onRetry = useCallback(async () => {
    if (recording) {
      await mutations.retry.run(recording.id);
      refetchSoon();
    }
  }, [recording, mutations, refetchSoon]);

  const onReprocess = useCallback(async () => {
    if (recording) {
      await mutations.reprocessAi.run(recording.id);
      refetchSoon();
    }
  }, [recording, mutations, refetchSoon]);

  const onUnlink = useCallback(async () => {
    if (recording) {
      await mutations.unlink.run(recording.id);
      refetchSoon();
    }
  }, [recording, mutations, refetchSoon]);

  const onDelete = useCallback(() => {
    const zoomRecordingId = recording?.zoomRecording?.id;
    if (!zoomRecordingId) {
      return;
    }
    const confirmString = `DELETE ${zoomRecordingId}`;
    showPopup(PopupNames.zoomDangerConfirm, {
      title: `${t('pages.zoom.lessons.deleteTitle')}`,
      body: `${t('pages.zoom.lessons.deleteBody')}`,
      confirmString,
      onConfirm: async () => {
        await mutations.deleteRecording.run(zoomRecordingId, confirmString);
        refetchSoon();
      },
    });
  }, [recording, showPopup, mutations, refetchSoon, t]);

  if (loading && !lesson) {
    return (
      <Wrapper>
        <CenteredPanel>
          <Spin />
        </CenteredPanel>
      </Wrapper>
    );
  }

  if (!lesson) {
    return (
      <Wrapper>
        <BackLink type="button" onClick={() => history.push(PATHS.zoomLessons)}>
          ← {t('pages.zoom.lessons.back')}
        </BackLink>
        <CenteredPanel>{t('pages.zoom.lessons.notFound')}</CenteredPanel>
      </Wrapper>
    );
  }

  const retryBlocked = [
    ZoomStatus.DOWNLOADING,
    ZoomStatus.UPLOADED,
    ZoomStatus.AI_PROCESSING,
    ZoomStatus.SKIPPED,
  ].includes(status as ZoomStatus);
  const reprocessAllowed =
    [ZoomStatus.AI_COMPLETED, ZoomStatus.PUBLISHED, ZoomStatus.FAILED].includes(
      status as ZoomStatus,
    ) && Boolean(recording?.transcriptS3Key);
  const unlinkBlocked = [
    ZoomStatus.DOWNLOADING,
    ZoomStatus.UPLOADED,
    ZoomStatus.AI_PROCESSING,
  ].includes(status as ZoomStatus);

  const group = lesson.module?.group;

  return (
    <Wrapper>
      <BackLink type="button" onClick={() => history.push(PATHS.zoomLessons)}>
        ← {t('pages.zoom.lessons.back')}
      </BackLink>

      <HeaderPanel>
        <HeaderMain>
          <LessonName>{lesson.name}</LessonName>
          <GroupLine>
            {group?.name}
            {group?.course?.name ? ` · ${group.course.name}` : ''}
          </GroupLine>
          <Chips>
            <Chip>📅 {lesson.date}</Chip>
            <Chip>
              🕒 {lesson.startTime?.slice(0, 5)}–{lesson.endTime?.slice(0, 5)}
            </Chip>
            <ZoomStatusBadge status={status ?? ZoomStatus.NO_RECORDING} />
          </Chips>
        </HeaderMain>
        {recording ? (
          <HeaderActions>
            <Tooltip title={t('pages.zoom.lessons.actions.retryHint')}>
              <Button type="primary" disabled={retryBlocked} onClick={onRetry}>
                {status === ZoomStatus.MATCHED
                  ? t('pages.zoom.lessons.actions.startProcessing')
                  : t('pages.zoom.lessons.actions.retry')}
              </Button>
            </Tooltip>
            <Button disabled={!reprocessAllowed} onClick={onReprocess}>
              {t('pages.zoom.lessons.actions.reprocess')}
            </Button>
            <Button disabled={unlinkBlocked} onClick={onUnlink}>
              {t('pages.zoom.lessons.actions.unlink')}
            </Button>
            <Button danger onClick={onDelete}>
              {t('pages.zoom.lessons.actions.delete')}
            </Button>
          </HeaderActions>
        ) : null}
      </HeaderPanel>

      {recording ? (
        <StatusPanel>
          <PanelTitle>{t('pages.zoom.lessons.pipeline')}</PanelTitle>
          <PipelineStepper
            status={status as ZoomStatus}
            errorMessage={recording.errorMessage}
          />
        </StatusPanel>
      ) : (
        <StatusPanel>
          <ZoomStatusBadge status={ZoomStatus.NO_RECORDING} />
          <NoRecordingHint>
            {t('pages.zoom.lessons.noRecordingHint')}
          </NoRecordingHint>
        </StatusPanel>
      )}

      {recording ? (
        <LogPanel>
          <LogHeader>
            <PanelTitle style={{ margin: 0 }}>
              {t('pages.zoom.processingLog.title')}
            </PanelTitle>
            <Button
              onClick={() =>
                showPopup(PopupNames.zoomProcessingLog, { lessonId: id })
              }
            >
              {t('pages.zoom.processingLog.fullLog')}
            </Button>
          </LogHeader>
          <ProcessingLogTimeline
            lessonId={id}
            pollInterval={isPollable ? POLL_INTERVAL : 0}
          />
        </LogPanel>
      ) : null}

      <Grid single={!recording}>
        <Column>
          <Panel>
            <PanelTitle>{t('pages.zoom.lessons.videoTitle')}</PanelTitle>
            {lesson.video ? (
              <>
                <Video controls preload="metadata" src={lesson.video} />
                <VideoLink href={lesson.video} target="_blank" rel="noreferrer">
                  {t('pages.zoom.lessons.columns.videoOpen')} ↗
                </VideoLink>
              </>
            ) : (
              <VideoEmpty>
                <VideoEmptyIcon>🎥</VideoEmptyIcon>
                {t('pages.zoom.lessons.noVideo')}
              </VideoEmpty>
            )}
          </Panel>

          {lesson.lmsDescription ? (
            <Panel>
              <PanelTitle>{t('pages.zoom.lessons.lessonInfo')}</PanelTitle>
              <Description
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: lesson.lmsDescription }}
              />
            </Panel>
          ) : null}

          {recording ? (
            <Panel>
              <PanelTitle>{t('pages.zoom.lessons.aiSummary')}</PanelTitle>
              <AiSummaryViewer
                markdown={recording.aiSummary}
                model={recording.aiModel}
              />
            </Panel>
          ) : null}
        </Column>

        {recording ? (
          <Column>
            <Panel>
              <PanelTitle>{t('pages.zoom.lessons.metadata')}</PanelTitle>
              <MetaGrid>
                <Meta label={t('pages.zoom.lessons.columns.matchedBy')}>
                  {recording.matchedBy}
                  {recording.matchedByUser
                    ? ` · ${recording.matchedByUser.email}`
                    : ''}
                </Meta>
                <Meta label={t('pages.zoom.lessons.columns.retryCount')}>
                  {recording.retryCount}
                </Meta>
                <Meta label={t('pages.zoom.lessons.lastAttempt')}>
                  {formatWarsaw(recording.lastAttemptAt)}
                </Meta>
                <Meta label={t('pages.zoom.lessons.updatedAt')}>
                  {formatWarsaw(recording.updatedAt)}
                </Meta>
              </MetaGrid>
            </Panel>

            <Panel>
              <PanelTitle>{t('pages.zoom.lessons.artifacts')}</PanelTitle>
              <ArtifactList>
                <Artifact icon="📄" value={recording.transcriptS3Key} t={t} />
                <Artifact icon="💬" value={recording.chatS3Key} t={t} />
                <Artifact icon="🎵" value={recording.audioS3Key} t={t} />
              </ArtifactList>
            </Panel>

            {recording.errorMessage ? (
              <Panel>
                <PanelTitle>{t('pages.zoom.lessons.errorTitle')}</PanelTitle>
                <ErrorBlock>{recording.errorMessage}</ErrorBlock>
              </Panel>
            ) : null}
          </Column>
        ) : null}
      </Grid>
    </Wrapper>
  );
});

interface IMetaProps {
  label: string;
  children: React.ReactNode;
}

const Meta = ({ label, children }: IMetaProps) => (
  <MetaItem>
    <MetaLabel>{label}</MetaLabel>
    <MetaValue>{children}</MetaValue>
  </MetaItem>
);

interface IArtifactProps {
  icon: string;
  value?: string | null;
  t: (key: string) => string;
}

const Artifact = ({ icon, value, t }: IArtifactProps) => (
  <ArtifactRow>
    <span>{icon}</span>
    {value ? (
      <ArtifactName title={value}>{value}</ArtifactName>
    ) : (
      <Muted>{t('pages.zoom.lessons.noArtifact')}</Muted>
    )}
  </ArtifactRow>
);

const Wrapper = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 16px 0 40px;
`;

const CenteredPanel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  margin-top: 12px;
  color: ${Colors.Gray};
`;

const BackLink = styled.button`
  border: none;
  background: none;
  color: ${Colors.Blue};
  cursor: pointer;
  padding: 0;
  font-size: 14px;
`;

const Panel = styled.div`
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 2px ${Colors.BlueShadow};
`;

const HeaderPanel = styled(Panel)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
  margin: 12px 0 20px;
`;

const HeaderMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 240px;
`;

const LessonName = styled.h2`
  ${getFont({ fontSize: 22, lineHeight: 28, familyWeight: 'bold' })};
  color: ${Colors.Black};
  margin: 0;
`;

const GroupLine = styled.div`
  font-size: 14px;
  color: ${Colors.Gray};
`;

const Chips = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const Chip = styled.span`
  background: ${Colors.BasicBlueLight};
  color: ${Colors.Black};
  border-radius: 14px;
  padding: 4px 12px;
  font-size: 13px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const StatusPanel = styled(Panel)`
  margin-bottom: 20px;
`;

const LogPanel = styled(Panel)`
  margin-bottom: 20px;
`;

const LogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const Grid = styled.div<{ single?: boolean }>`
  display: grid;
  grid-template-columns: ${({ single }) =>
    single ? '1fr' : 'minmax(0, 1fr) 340px'};
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

const PanelTitle = styled.h3`
  ${getFont({ fontSize: 14, lineHeight: 18, familyWeight: 'semibold' })};
  color: ${Colors.Black};
  margin: 0 0 14px;
`;

const Video = styled.video`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${Colors.PrimaryBlack};
  border-radius: 8px;
  display: block;
`;

const VideoLink = styled.a`
  display: inline-block;
  margin-top: 10px;
  font-size: 13px;
  color: ${Colors.Blue};
`;

const VideoEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  aspect-ratio: 16 / 9;
  background: ${Colors.MainBackground};
  border: 1px dashed ${Colors.SecondLightGray};
  border-radius: 8px;
  color: ${Colors.Gray};
  font-size: 14px;
`;

const VideoEmptyIcon = styled.div`
  font-size: 32px;
`;

const Description = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${Colors.Black};

  & img {
    max-width: 100%;
  }
`;

const NoRecordingHint = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  color: ${Colors.Gray};
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  color: ${Colors.Gray};
`;

const MetaValue = styled.span`
  font-size: 14px;
  color: ${Colors.Black};
  word-break: break-word;
`;

const ArtifactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ArtifactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const ArtifactName = styled.span`
  color: ${Colors.Black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Muted = styled.span`
  color: ${Colors.Gray};
`;

const ErrorBlock = styled.pre`
  background: #fef2f2;
  color: ${Colors.ErrorDefault};
  padding: 12px;
  border-radius: 8px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  margin: 0;
`;
